import { QuestionBar } from './../../../../models/reporting/question-bar.model';
import { OverallRating } from './../../../../models/reporting';
import { Component, OnInit, AfterViewInit, Input } from '@angular/core';
import * as Chart from 'chart.js';
import { MathRoundHelper } from '../../../../helpers';

@Component({
  selector: 'app-overall-rating-module',
  templateUrl: './overall-rating-module.component.html',
  styles: []
})
export class OverallRatingModuleComponent implements OnInit, AfterViewInit {
  @Input() scaleDescription: string;
  @Input() overallRating: OverallRating;

  canvas: any;
  ctx: any;
  pieWidth: number;

  private barColors: { overallColor: string, comparisonColor: string, selfColor: string } =
    { overallColor: 'rgb(36, 142, 206)', comparisonColor: 'rgb(152, 169, 179)', selfColor: 'rgb(174, 31, 119)' };
  private yAxisIncrementer = 4;
  constructor() { }

  ngOnInit() {    
    const canvasArea = document.getElementsByClassName('line-canvas');
    this.pieWidth = canvasArea[0].clientWidth;
  }

  ngAfterViewInit() {     
    if (!this.overallRating) {
      return;
    }
    const displayingOverallValue = this.getRate(this.overallRating.overallExcludeSelf);
    const displayingComparisonValue = this.getRate(this.overallRating.comparisonGroup);
    const displayingSelfValue = this.getRate(this.overallRating.self);
    this.addAverageOverallToQuestionBars(this.overallRating.questionBars, displayingOverallValue);
    const overAllDatasets: Array<Chart.ChartDataSets> = this.setOverallDatasets(this.overallRating.questionBars, displayingOverallValue);
    const maxY = (overAllDatasets) ? Math.max(...this.overallRating.questionBars.map(x => x.respondentCount)) + this.yAxisIncrementer : 5;
    const comparisonDataset: Chart.ChartDataSets = (displayingComparisonValue !== 0) ?
      this.setSingleDataset(this.overallRating.comparisonGroup, this.barColors.comparisonColor, displayingComparisonValue, maxY) : {};
    const selfDataset: Chart.ChartDataSets = (displayingSelfValue !== 0) ? this.setSingleDataset(this.overallRating.self,
      this.barColors.selfColor, displayingSelfValue, maxY) : {};
    this.setMaxYAxisValue(overAllDatasets, displayingOverallValue, maxY);
    const commonDatasets = (overAllDatasets) ? [selfDataset, comparisonDataset].concat(overAllDatasets) : [selfDataset, comparisonDataset];
    this.buildBarChart(commonDatasets, maxY);   
  }

  getRate(rating: number): number {
    return MathRoundHelper.roundToFixedNumber(rating, 1);
  }

  private setOverallDatasets(questionBars: Array<QuestionBar>, displayingOverallValue: number): Array<Chart.ChartDataSets> {
    if (questionBars.length === 0 || questionBars.every(x => x.averageValue === 0)) {
      return null;
    }
    const barColor = this.barColors.overallColor;
    const dataset: Chart.ChartDataSets = this.setCommonDataset(barColor);
    return questionBars.map(x => {
      dataset.data = [{ x: x.averageValue, y: x.respondentCount }, { x: x.averageValue, y: 0 }];
      const dataLabels = this.setDatasetsDatalabels(barColor, 'white', 9, true, displayingOverallValue, -2);
      dataset['datalabels'] = dataLabels;
      return Object.assign({}, dataset);
    });
  }

  private buildBarChart(datasets: Array<Chart.ChartDataSets>, maxY: number) {
    this.canvas = document.getElementById('myChart');
    if (!this.canvas) {
      return;
    }
    this.ctx = this.canvas.getContext('2d');
    window['lineChart'] = new Chart(this.ctx, {
      type: 'line',
      data: {
        datasets: datasets
      },
      options: {
        tooltips: {
          enabled: false
        },
        animation: {
          onComplete: canvasToImg
        },
        responsive: false,
        scales: {
          xAxes: [this.setXAxisStyle()],
          yAxes: [this.setYAxisStyle(maxY)]
        },
        legend: { display: false },
        plugins: {
          datalabels: this.setCommonDatalabels('start', 'left')
        }
      }
    });
  }

  private setXAxisStyle(): Chart.ChartXAxe {
    return {
      type: 'linear',
      position: 'bottom',
      ticks: {
        beginAtZero: true,
        max: this.overallRating.maxScaleValue
      }
    };
  }

  private setYAxisStyle(maxY: number): Chart.ChartYAxe {
    return {
      ticks: {
        max: maxY + this.yAxisIncrementer / 2,
        display: false,
        min: -maxY / 2 - this.yAxisIncrementer
      }
    };
  }

  private setSingleDataset(ratingValue: number, barColor: string,
    displayingValue: number, maxYaxis: number): Chart.ChartDataSets {
    const dataset: Chart.ChartDataSets = this.setCommonDataset(barColor);
    const xValue = (this.overallRating.questionBars.some(x => x.averageValue === ratingValue)) ?
      ratingValue + 0.1 : ratingValue;
    dataset.data = [{ x: xValue, y: maxYaxis / 2 }, {
      x: xValue, y: -maxYaxis / 2
    }];
    const dataLabels = this.setDatasetsDatalabels(barColor, barColor, 12, false, displayingValue, 3);
    dataLabels['backgroundColor'] = '';
    dataset['datalabels'] = dataLabels;
    return dataset;
  }

  private setCommonDataset(barColor: string, borderWidth = 3, pointRadius = 0, pointHitRadius = 0): Chart.ChartDataSets {
    return {
      fill: false,
      borderColor: barColor,
      borderWidth: borderWidth,
      pointRadius: pointRadius,
      pointHitRadius: pointHitRadius
    };
  }

  private addAverageOverallToQuestionBars(questionBars: Array<QuestionBar>, displayingOverallValue: number) {
    const maxRespondentsCount = Math.max(...questionBars.map(x => x.respondentCount));
    questionBars.push({respondentCount: maxRespondentsCount, averageValue: displayingOverallValue});
  }

  private setCommonDatalabels(anchor: string, align: string, topPadding = 1, bottomPadding = 1, leftPadding = 6, rightPadding = 6) {
    return {
      anchor: anchor,
      align: align,

      padding: {
        top: topPadding,
        bottom: bottomPadding,
        left: leftPadding,
        right: rightPadding
      }
    };
  }

  private setDatasetsDatalabels(barColor: string, fontColor: string, fontSize: number,
    isOverall: boolean, displayValue?: number,
    offset = 0, fontWeight: string | number = 'bold') {
    return {
      offset: offset,
      color: fontColor,
      font: {
        weight: fontWeight,
        size: fontSize
      },
      backgroundColor: barColor,
      display: (context) => (isOverall) ? this.displayOverallLabels(context) : context.dataIndex === 1,
      formatter: (value, context) => (isOverall) ? this.formatterOverallLabels(value, context, displayValue) : displayValue
    };
  }

  private displayOverallLabels(context: any) {
    return context.dataIndex === 0;
  }

  private formatterOverallLabels(value: any, context: any, displayValue: number) {
    if (value.x !== displayValue) {
      return null;
    }
    return displayValue;
  }

  private setMaxYAxisValue(overAllDatasets: Array<Chart.ChartDataSets>, displayingOverallValue: number, maxY: number) {
    if (!overAllDatasets) {
      return;
    }
    overAllDatasets.forEach(x => {
      const chartData: Array<Chart.ChartPoint> = x.data as Array<Chart.ChartPoint>;
      chartData.forEach((chartPoint: Chart.ChartPoint) => {
        if (chartPoint.x === displayingOverallValue && chartPoint.y !== 0) {
          chartPoint.y = maxY;
        }
      });
    });
  }
}

function canvasToImg() {
        const canvas: any = window['lineChart'];
        const url = canvas.toBase64Image();
        document.getElementById(`lineChart`)['src'] = url;

}